import axios from "axios";
import { toast } from "sonner";
import { playAlertSound } from "../../utils/alertSound";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Action handlers (resolve / delete / vote / alert-created) for the
 * Dashboard. Returns plain callbacks; relies on the caller for state.
 */
export function useAlertActions({ userLocation, fetchAlerts, fetchStats, setShowCreate }) {
  const handleAlertCreated = () => {
    setShowCreate(false);
    fetchAlerts();
    fetchStats();
    playAlertSound();
    toast.success("Emergency alert sent!");
  };

  const handleResolve = async (alertId) => {
    try {
      await axios.patch(`${API}/alerts/${alertId}/resolve`, {}, { withCredentials: true });
      fetchAlerts();
      fetchStats();
      toast.success("Alert resolved");
    } catch {
      toast.error("Failed to resolve alert");
    }
  };

  const handleDelete = async (alertId) => {
    try {
      if (alertId === "__CLEAR_ALL__") {
        const r = await axios.delete(`${API}/alerts`, { params: { status_filter: "active" }, withCredentials: true });
        toast.success(`Cleared ${r.data.deleted} alert${r.data.deleted === 1 ? "" : "s"}`);
      } else {
        await axios.delete(`${API}/alerts/${alertId}`, { withCredentials: true });
        toast.success("Alert deleted");
      }
      fetchAlerts();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete alert");
    }
  };

  const handleVote = async (alertId, vote) => {
    if (!userLocation) { toast.error("GPS required to vote — set your location on the map"); return; }
    try {
      const r = await axios.post(
        `${API}/alerts/${alertId}/vote`,
        { vote, latitude: userLocation.latitude, longitude: userLocation.longitude },
        { withCredentials: true },
      );
      toast.success(vote === "true" ? "Marked as genuine" : "Marked as false");
      if (r.data.flagged_false) {
        toast.warning("Alert is now flagged false — sender has been sanctioned");
      }
      fetchAlerts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Vote failed");
    }
  };

  return { handleAlertCreated, handleResolve, handleDelete, handleVote };
}
